import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { FunctionWarning, Severity } from '../../services/performanceApi';

export const fmtMs = (ms?: number | null) => {
  if (ms === undefined || ms === null) return '–';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

export const fmtS = (s?: number | null) => (s === undefined || s === null ? '–' : `${s < 10 ? s.toFixed(1) : Math.round(s)}s`);

export const fmtPct = (share?: number | null, digits = 1) =>
  share === undefined || share === null ? '–' : `${(share * 100).toFixed(share > 0 && share < 0.001 ? 2 : digits)}%`;

export const fmtNum = (n?: number | null) => (n === undefined || n === null ? '–' : n.toLocaleString());

export const fmtCredits = (n?: number | null) =>
  n === undefined || n === null ? '–' : n < 0.01 && n > 0 ? n.toFixed(4) : n.toFixed(2);

export const fmtBucket = (iso: string, unit: 'hour' | 'day' = 'day') => {
  const d = new Date(iso);
  return unit === 'hour'
    ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const severityColor = (s: Severity): 'error' | 'warning' | 'info' => s;

export const healthColor = (state?: string | null): 'success' | 'warning' | 'error' | 'default' =>
  state === 'healthy' ? 'success' : state === 'degraded' ? 'warning' : state === 'down' ? 'error' : 'default';

export const WarningChips: React.FC<{ warnings?: FunctionWarning[]; max?: number }> = ({ warnings, max = 3 }) => {
  if (!warnings?.length) return null;
  const shown = warnings.slice(0, max);
  const rest = warnings.slice(max);
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {shown.map(w => (
        <Tooltip key={w.code} title={w.detail}>
          <Chip label={w.label} size="small" color={severityColor(w.severity)} variant="outlined" sx={{ cursor: 'help' }} />
        </Tooltip>
      ))}
      {rest.length > 0 && (
        <Tooltip title={rest.map(w => w.label).join(' · ')}>
          <Chip label={`+${rest.length}`} size="small" variant="outlined" />
        </Tooltip>
      )}
    </Stack>
  );
};

export const StatCard: React.FC<{ label: string; value: React.ReactNode; caption?: React.ReactNode; tooltip?: string }> = ({
  label,
  value,
  caption,
  tooltip,
}) => (
  <Tooltip title={tooltip || ''} disableHoverListener={!tooltip}>
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5">{value}</Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary" component="div">
            {caption}
          </Typography>
        )}
      </CardContent>
    </Card>
  </Tooltip>
);

export const SectionTitle: React.FC<{ title: string; help?: string; action?: React.ReactNode }> = ({ title, help, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 1, flexWrap: 'wrap' }}>
    <Box>
      <Typography variant="h6">{title}</Typography>
      {help && (
        <Typography variant="caption" color="text.secondary">
          {help}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);
